import * as Sentry from '@sentry/react';
import { bannerErrors } from 'app/client/store/actions/errorActions';

/**
 * Check if an error should stop the component from rendering.
 *
 * Only the errors that make the data of a component unusable are fatal: a rate limit, a network
 * error and a server error, plus an error without a status, which is a request that threw instead
 * of responding. A status like a 404 or a 403 is about a single (related) resource, the component
 * handles a missing resource itself and should still render.
 *
 * @param {Object} error The error to check.
 *
 * @returns {boolean} True when the error is fatal.
 */
const isFatal = ({ status }) => typeof status === 'undefined' || Boolean(bannerErrors[status]);

/**
 * Check if the API refused the extracted resource. A fatal error is not a missing resource: the
 * layout shows it in its banner.
 */
export const isMissing = (extracted) => {
  const errors = extracted.request()?.errors || [];

  return !extracted.pending() && errors.length > 0 && !errors.some(isFatal);
};

/**
 * Get the errors of a failed request, if there are any.
 *
 * A failed request rejects with its errors, or with the resources when it already failed before and
 * is known in the store. It resolves with the raw body of each performed request when it does not
 * reject at all, which is the case for a request without relations and for a failed relation. Those
 * bodies are nested, a request resolves with its own body followed by the body of each relation.
 *
 * @param {*} value The resolved or rejected value of the request.
 *
 * @returns {array|undefined} The errors of the failed request.
 */
export const getErrors = (value) => {
  const failed = [value].flat(Infinity).find((item) => item?.errors || item?.resources);

  return failed?.errors || failed?.resources;
};

/**
 * Get the errors of a failed request when at least one of them is fatal.
 *
 * @param {*} value The resolved or rejected value of the request.
 *
 * @returns {array|undefined} The errors of the failed request.
 */
const getFatalErrors = (value) => {
  const failed = [value]
    .flat(Infinity)
    .find((item) => (item?.errors || item?.resources)?.some(isFatal));

  return failed?.errors || failed?.resources;
};

/**
 * Wrap a fetcher method to reject a failed request with its errors.
 *
 * @param {Function} method The fetcher method to wrap.
 *
 * @returns {Function} The wrapped fetcher method.
 */
const rejectFailedRequest =
  (method) =>
  (...args) =>
    Promise.resolve(method(...args)).then(
      (response) => {
        const errors = getFatalErrors(response);

        if (errors) {
          return Promise.reject({ errors });
        }

        // The fetcher client fails on any errors in the response, fatal or not.
        return response?.errors ? null : response;
      },
      (error) => {
        const errors = getErrors(error);

        // A fetcher that reads the data of a failed request throws instead of rejecting with an error.
        // Report those to keep them visible as the bug they are, but still render an error.
        if (!errors) {
          Sentry.captureException(error);

          return Promise.reject({ errors: [{}] });
        }

        const fatal = getFatalErrors(error);

        // Resolve without data when none of the errors is fatal, so the component still renders.
        return fatal ? Promise.reject({ errors: fatal }) : null;
      },
    );

/**
 * Wrap the methods of the fetcher to reject a failed request with its errors.
 *
 * Without this a request that resolves with an error body, or that rejects with the resources of an
 * earlier failure, is handled as if it succeeded and the component renders without its data.
 *
 * @param {Object} fetcher The fetcher settings.
 *
 * @returns {Object} The fetcher settings with the wrapped methods.
 */
export const rejectFailedRequests = ({ onMount, onUpdate, ...settings }) => ({
  ...settings,
  ...(onMount ? { onMount: rejectFailedRequest(onMount) } : {}),
  ...(onUpdate ? { onUpdate: rejectFailedRequest(onUpdate) } : {}),
});
